import type { Country, SimpleUser, User } from "@daytrip/legacy-models";
import { DriversCompany } from "@daytrip/legacy-models";
import { RetrieveDriversCompaniesOptions, RetrieveDriversOptions } from "@daytrip/legacy-options";
import { isUndefinedOrNull } from "@daytrip/utils";
import autobind from "autobind-decorator";
import { plainToClass } from "class-transformer";
import { validate } from "class-validator";
import { action, observable } from "mobx";
import type { Option } from "react-select-legacy";

import { PageStore } from "../../stores/PageStore";
import { apiTrpcClient } from "@/trpc/api";

import type { DriversCompaniesPageRouter } from "./DriversCompaniesPageRouter";

@autobind
export class DriversCompaniesPageStore extends PageStore<DriversCompaniesPageRouter, null> {
    @observable
    public driversCompanies: Array<User>;

    @observable
    public driversCompaniesCount: number = 0;

    @observable
    public countries?: Array<Country>;

    @observable
    public managers?: Array<SimpleUser>;

    @observable
    public isLoading: boolean = false;

    @action
    public async onFetchData() {
        await this.fetchDriversCompanies();
        this.fetchContent();
    }

    @action
    public async fetchContent() {
        [this.countries, this.managers] = await Promise.all([
            this.rpcClient.content.retrieveCountries({}),
            apiTrpcClient.user.getSimpleRegionalManagers.query({}),
        ]);

        const queryVehicleModelIds = this.pageRouter.vehicleModels;
        if (queryVehicleModelIds?.length) {
            const vehicleModels = await this.rpcClient.vehicle.retrieveVehicleModelsById(queryVehicleModelIds);
            this.selectedVehicleModelOptions = vehicleModels.map((model) => ({
                value: model.modelId,
                label: model.name,
            }));
        }
    }

    @action
    public async fetchDriversCompanies(): Promise<void> {
        this.isLoading = true;
        this.driversCompanies = [];

        const options: RetrieveDriversCompaniesOptions = {
            skip: this.pageRouter.skip,
            limit: this.pageRouter.limit,
            sortBy: this.pageRouter.sortBy as keyof User,
            sortDirection: this.pageRouter.sortDirection,
            searchString: this.pageRouter.search,
            managerIds: this.pageRouter.managerIds,
            countryIds: this.pageRouter.countryIds,
            statusIn: this.pageRouter.statuses,
            payoutType: this.pageRouter.payoutType,
            hasMangopayInformationApproved: this.pageRouter.hasMangopayInformationApproved,
            vehicleModelIds: this.pageRouter.vehicleModels,
            isActiveInLast7Days: this.pageRouter.isActiveInLast7Days,
            isWillingToRentEnabled: this.pageRouter.isWillingToRentEnabled,
            hasPendingDocuments: this.pageRouter.hasPendingDocuments,
        };

        const optionsValidationErrors = await validate(plainToClass(RetrieveDriversCompaniesOptions, options), {
            skipMissingProperties: true,
            forbidUnknownValues: false,
        });
        if (optionsValidationErrors.length > 0) {
            return Promise.reject(new Error("Validation error"));
        }

        const driversCompanies = await apiTrpcClient.driversCompany.retrieveDriversCompanies.query(options);
        this.driversCompanies = driversCompanies.map((user) => {
            user.driversCompany = plainToClass(DriversCompany, user.driversCompany);
            return user;
        });

        this.driversCompaniesCount = await apiTrpcClient.driversCompany.retrieveDriversCompaniesCount.query(options);
        this.isLoading = false;
    }

    @observable
    public isEmailsLoading = false;

    @observable
    public isExportEmailsModalOpened = false;

    @observable
    public exportedEmails = "";

    @action
    public async exportEmails(): Promise<void> {
        this.isExportEmailsModalOpened = true;
        this.isEmailsLoading = true;

        const options: RetrieveDriversOptions = plainToClass(RetrieveDriversOptions, {
            searchString: this.pageRouter.search,
            countryIds: isUndefinedOrNull(this.pageRouter.countryIds) ? undefined : this.pageRouter.countryIds,
            isDriversCompany: true,
            statusIn: isUndefinedOrNull(this.pageRouter.statuses) ? undefined : this.pageRouter.statuses,
        });

        const optionsValidationErrors = await validate(options, {
            skipMissingProperties: true,
            forbidUnknownValues: false,
        });
        if (optionsValidationErrors.length > 0) {
            return Promise.reject(new Error("Validation error"));
        }

        const arrayOfEmails = await this.rpcClient.driver.retrieveEmailsForExport(options);

        this.exportedEmails = arrayOfEmails.join("; ");

        this.isEmailsLoading = false;
    }

    @action
    public closeExportEmailsModal(): void {
        this.isExportEmailsModalOpened = false;
    }

    public async loadVehicleModels(search: string) {
        if (search.length <= 2) {
            return { options: [], complete: false };
        }

        const vehicleModels = await this.rpcClient.vehicle.searchVehicleModels(search);

        return {
            options: vehicleModels.map((vehicleModel) => ({
                value: vehicleModel.modelId,
                label: vehicleModel.name,
            })),
            complete: true,
        };
    }

    @observable
    public selectedVehicleModelOptions: Option[] = [];

    @action
    public selectVehicleModels(selectedOptions: Option<string>[]): void {
        this.selectedVehicleModelOptions = selectedOptions;
        this.pageRouter.filterVehicleModel(selectedOptions?.map((option) => option.value) ?? []);
    }

    public isDataFetched(): this is DriversCompaniesPageStore & { driversCompanies: User[] } {
        return !!this.driversCompanies;
    }
}
